import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { Form, Select, Card, Spin } from 'antd';
import { Button } from 'components';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCarousels from '../useCarousels';

const { Option } = Select;

export const CarouselForm = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const {
    companies,
    shoppingCenters,
    loadingCompanies,
    createCarousel,
    updateCarousel,
    fetchCarousel,
  } = useCarousels();

  const currentShoppingCenter = Form.useWatch('shopping_center_id', form);
  const currentShoppingCenterTitle = shoppingCenters.find(
    (center) => center.value === currentShoppingCenter,
  )?.title;
  const companiesByShoppingCenter = useMemo(() => {
    return companies.filter(
      (company) => company.shopping_center_title === currentShoppingCenterTitle,
    );
  }, [companies, currentShoppingCenterTitle]);
  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode) {
      const loadCarousel = async () => {
        setLoading(true);
        try {
          const carousel = await fetchCarousel(id);
          if (carousel) {
            const shoppingCenterId =
              carousel.shopping_center_id || carousel.shopping_center?.id;
            const companyId = carousel.company?.id;

            form.setFieldsValue({
              shopping_center_id: shoppingCenterId,
              company_id: companyId,
            });
          }
        } catch (error) {
          console.error('Error loading carousel:', error);
        } finally {
          setLoading(false);
        }
      };

      loadCarousel();
    }
  }, [id, isEditMode, fetchCarousel, form]);

  const handleSubmit = useCallback(
    async (values) => {
      setLoading(true);
      try {
        const formData = {
          shopping_center_id: values.shopping_center_id,
          company_id: values.company_id,
        };

        let result;
        if (isEditMode) {
          result = await updateCarousel(id, formData);
        } else {
          result = await createCarousel(formData);
        }
        if (result) {
          navigate('/website/carousels');
        }
      } catch (error) {
        console.error('Error saving carousel:', error);
      } finally {
        setLoading(false);
      }
    },
    [isEditMode, id, createCarousel, updateCarousel, navigate],
  );

  if (loading && isEditMode) {
    return (
      <Card title={t('Caricamento...')}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size='large' />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        isEditMode
          ? t('Modifica Slider loghi HP')
          : t('Crea Nuovo Slider loghi HP')
      }
      extra={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/website/carousels')}
        >
          {t('Indietro')}
        </Button>
      }
    >
      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        initialValues={{
          shopping_center_id: undefined,
          company_id: undefined,
        }}
      >
        <div style={{ marginBottom: '24px' }}>
          <Form.Item
            name='shopping_center_id'
            label={t('Outlet Village')}
            rules={[
              { required: true, message: t('Seleziona un Outlet Village') },
            ]}
          >
            <Select
              placeholder={t('Seleziona Outlet Village')}
              loading={loadingCompanies}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {shoppingCenters
                .filter(
                  (center) =>
                    center &&
                    (center.value || center.id) &&
                    (center.title || center.name),
                )
                .map((center) => (
                  <Option
                    key={center.value || center.id}
                    value={center.value || center.id}
                  >
                    {center.title || center.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </div>
        <div style={{ marginBottom: '24px' }}>
          <Form.Item
            name='company_id'
            label={t('Insegna')}
            rules={[{ required: true, message: t('Seleziona un Insegna') }]}
          >
            <Select
              placeholder={t('Seleziona Insegna')}
              loading={loadingCompanies}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {companiesByShoppingCenter
                .filter(
                  (company) =>
                    company &&
                    (company.value || company.id) &&
                    (company.title || company.name),
                )
                .map((company) => (
                  <Option
                    key={company.value || company.id}
                    value={company.value || company.id}
                  >
                    {company.title || company.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </div>

        <Form.Item>
          <Button
            type='primary'
            width={'150px'}
            big
            htmlType='submit'
            loading={loading}
            icon={<SaveOutlined />}
            style={{ backgroundColor: '#002637', borderColor: '#002637' }}
          >
            {isEditMode ? t('Aggiorna') : t('Crea')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
});
